import * as React from "react";
import { createPortal } from "react-dom";
import { Award, Ban, Check, Minus, X } from "lucide-react";
import { ImageLoader } from "../../../ImageLoader";
import { CourseLessonCard } from "../../../../../app/components/certification/CourseLessonCard";
import { estimateLessonMinutes } from "../../../../../app/lib/media/courseDuration";
import type { CertificationTrainingUserProgress } from "../../../../../app/lib/api/certifications";
import { useT } from "../../../../../app/lib/i18n";
import * as styles from "./ListViewUserCertificationCard.style.css";

export type CertificationTrainingMilestone = {
  id: string;
  title?: string;
  section?: string;
  completed: boolean;
  stepNumber?: number;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  questionCount?: number;
  videoDurationSeconds?: number | null;
  userProgress?: CertificationTrainingUserProgress | null;
  /** High-water play percent for this lesson video (0–100). */
  videoWatchedPercent?: number;
};

export type CertificationTrainingProgressProps = {
  progressPercent: number;
  trainings: CertificationTrainingMilestone[];
  trophyImageUrl?: string | null;
  /** When true, the final (trophy) milestone is treated as complete. */
  moduleComplete?: boolean;
  /** Softens track colors and shows a prohibited overlay on the trophy. */
  expired?: boolean;
  /** Module completion timestamp — shown in the expired trophy tooltip. */
  completedAt?: string | null;
  /** Module expiry timestamp — shown in the expired trophy tooltip. */
  expiresAt?: string | null;
};

export type TrainingAttemptOutcome = "passed" | "failed" | "skipped" | "none";

export function getTrainingAttemptOutcome(
  training: CertificationTrainingMilestone,
  moduleComplete = false,
): TrainingAttemptOutcome {
  const progress = training.userProgress;
  if (progress) {
    return progress.passed ? "passed" : "failed";
  }
  if (training.completed || moduleComplete) return "passed";
  return "none";
}

/** Unattempted lessons before the furthest pass/fail are skipped (out of order). */
export function withSkippedGaps(
  outcomes: TrainingAttemptOutcome[],
): TrainingAttemptOutcome[] {
  let furthest = -1;
  for (let i = 0; i < outcomes.length; i++) {
    if (outcomes[i] === "passed" || outcomes[i] === "failed") furthest = i;
  }
  return outcomes.map((outcome, index) =>
    outcome === "none" && index < furthest ? "skipped" : outcome,
  );
}

export function furthestAttemptFillPercent(
  outcomes: TrainingAttemptOutcome[],
): number {
  let furthest = -1;
  for (let i = 0; i < outcomes.length; i++) {
    if (outcomes[i] === "passed" || outcomes[i] === "failed") furthest = i;
  }
  if (furthest < 0) return 0;
  if (furthest === outcomes.length - 1) return 100;
  const priorCount = Math.max(1, outcomes.length - 1);
  if (priorCount <= 1) return 12;
  return (furthest / (priorCount - 1)) * 88;
}

const OPEN_DELAY_MS = 800;
const CLOSE_DELAY_MS = 120;
const VIEWPORT_PAD = 8;
const GAP = 10;

function formatExactDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function MilestonePreviewPopover({
  training,
  index,
  anchorRect,
  onMouseEnter,
  onMouseLeave,
}: {
  training: CertificationTrainingMilestone;
  index: number;
  anchorRect: DOMRect;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const estimatedMinutes = estimateLessonMinutes({
    videoDurationSeconds: training.videoDurationSeconds,
    questionCount: training.questionCount ?? 0,
  });

  React.useLayoutEffect(() => {
    const update = () => {
      const popover = popoverRef.current;
      const width = popover?.offsetWidth ?? 360;
      const height = popover?.offsetHeight ?? 0;

      let left = anchorRect.left + anchorRect.width / 2 - width / 2;
      let top = anchorRect.bottom + GAP;

      if (left + width > window.innerWidth - VIEWPORT_PAD) {
        left = window.innerWidth - width - VIEWPORT_PAD;
      }
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

      if (top + height > window.innerHeight - VIEWPORT_PAD) {
        top = Math.max(VIEWPORT_PAD, anchorRect.top - height - GAP);
      }

      setPosition({ top, left });
    };

    update();
    const raf = requestAnimationFrame(update);
    const ro = popoverRef.current
      ? new ResizeObserver(() => update())
      : null;
    if (popoverRef.current && ro) ro.observe(popoverRef.current);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorRect]);

  return createPortal(
    <div
      ref={popoverRef}
      role="tooltip"
      className={styles.milestonePreviewPopover}
      style={
        position
          ? { top: position.top, left: position.left, opacity: 1 }
          : { top: 0, left: 0, opacity: 0, pointerEvents: "none" }
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.milestonePreviewBody}>
        <CourseLessonCard
          title={training.title ?? ""}
          section={training.section ?? ""}
          videoUrl={training.videoUrl}
          thumbnailUrl={training.thumbnailUrl}
          stepNumber={training.stepNumber ?? index + 1}
          estimatedMinutes={estimatedMinutes}
          questionCount={training.questionCount}
          userProgress={training.userProgress}
          videoWatchedPercent={training.videoWatchedPercent ?? 0}
          fallbackIndex={index}
          interactive={false}
          previewOnly
        />
      </div>
    </div>,
    document.body,
  );
}

function ExpiredTrophyTooltip({
  anchorRect,
  completedAt,
  expiresAt,
}: {
  anchorRect: DOMRect;
  completedAt?: string | null;
  expiresAt?: string | null;
}) {
  const t = useT();
  const tipRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const completedLabel = formatExactDate(completedAt);
  const expiredLabel = formatExactDate(expiresAt);

  React.useLayoutEffect(() => {
    const update = () => {
      const tip = tipRef.current;
      const width = tip?.offsetWidth ?? 240;
      const height = tip?.offsetHeight ?? 0;

      let left = anchorRect.left + anchorRect.width / 2 - width / 2;
      let top = anchorRect.bottom + GAP;

      if (left + width > window.innerWidth - VIEWPORT_PAD) {
        left = window.innerWidth - width - VIEWPORT_PAD;
      }
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

      if (top + height > window.innerHeight - VIEWPORT_PAD) {
        top = Math.max(VIEWPORT_PAD, anchorRect.top - height - GAP);
      }

      setPosition({ top, left });
    };

    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorRect]);

  return createPortal(
    <div
      ref={tipRef}
      role="tooltip"
      className={styles.expiryTooltip}
      style={
        position
          ? { top: position.top, left: position.left }
          : { top: 0, left: 0, opacity: 0 }
      }
    >
      <p className={styles.expiryTooltipTitle}>{t("shared.certification.expired")}</p>
      {expiredLabel ? (
        <p className={styles.expiryTooltipRow}>
          <span className={styles.expiryTooltipLabel}>{t("shared.certification.expiredOn")}</span>
          {expiredLabel}
        </p>
      ) : null}
      {completedLabel ? (
        <p className={styles.expiryTooltipRow}>
          <span className={styles.expiryTooltipLabel}>{t("shared.certification.originallyCompleted")}</span>
          {completedLabel}
        </p>
      ) : null}
    </div>,
    document.body,
  );
}

function MilestoneStatusIcon({
  outcome,
  size,
}: {
  outcome: Exclude<TrainingAttemptOutcome, "none">;
  size: number;
}) {
  if (outcome === "passed") {
    return <Check size={size} strokeWidth={3} aria-hidden />;
  }
  if (outcome === "failed") {
    return <X size={size} strokeWidth={3} aria-hidden />;
  }
  return <Minus size={size} strokeWidth={3} aria-hidden />;
}

function TrainingMilestone({
  training,
  index,
  isLast,
  outcome,
  trophyImageUrl,
  leftPercent,
  expired = false,
  completedAt,
  expiresAt,
}: {
  training: CertificationTrainingMilestone;
  index: number;
  isLast: boolean;
  outcome: TrainingAttemptOutcome;
  trophyImageUrl?: string | null;
  leftPercent: number;
  expired?: boolean;
  completedAt?: string | null;
  expiresAt?: string | null;
}) {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
  const openTimer = React.useRef<number | null>(null);
  const closeTimer = React.useRef<number | null>(null);

  const clearTimers = React.useCallback(() => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  const scheduleOpen = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (open) return;
    if (openTimer.current != null) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) {
        setAnchorRect(rect);
        setOpen(true);
      }
    }, expired && isLast ? 200 : OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
    }
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const keepOpen = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  let marker: React.ReactNode;
  if (isLast) {
    if (expired) {
      marker = (
        <div
          className={[styles.progressTrophy, styles.progressTrophyExpired].join(
            " ",
          )}
        >
          {trophyImageUrl ? (
            <ImageLoader
              src={trophyImageUrl}
              alt=""
              objectFit="contain"
              className={styles.progressTrophyImage}
            />
          ) : (
            <Award size={24} style={{ color: "#9CA3AF" }} />
          )}
          <span className={styles.progressTrophyProhibited} aria-hidden>
            <Ban size={42} strokeWidth={2.25} />
          </span>
        </div>
      );
    } else {
      const trophyClass = [
        styles.progressTrophy,
        outcome === "passed"
          ? styles.progressTrophyPassed
          : outcome === "failed"
            ? styles.progressTrophyFailed
            : outcome === "skipped"
              ? styles.progressTrophySkipped
              : styles.progressTrophyIncomplete,
      ].join(" ");

      marker = (
        <div className={trophyClass}>
          {outcome === "passed" ||
          outcome === "failed" ||
          outcome === "skipped" ? (
            <span className={styles.progressDotIcon}>
              <MilestoneStatusIcon outcome={outcome} size={22} />
            </span>
          ) : trophyImageUrl ? (
            <ImageLoader
              src={trophyImageUrl}
              alt=""
              objectFit="contain"
              className={[
                styles.progressTrophyImage,
                styles.progressTrophyImageIncomplete,
              ].join(" ")}
            />
          ) : (
            <Award size={24} style={{ opacity: 0.45 }} />
          )}
        </div>
      );
    }
  } else {
    const dotClass = [
      styles.progressDot,
      expired
        ? outcome === "passed"
          ? styles.progressDotPassedExpired
          : outcome === "failed"
            ? styles.progressDotFailedExpired
            : styles.progressDotIncompleteExpired
        : outcome === "passed"
          ? styles.progressDotPassed
          : outcome === "failed"
            ? styles.progressDotFailed
            : outcome === "skipped"
              ? styles.progressDotSkipped
              : styles.progressDotIncomplete,
    ].join(" ");

    marker = (
      <span className={dotClass} aria-hidden>
        {(outcome === "passed" ||
          outcome === "failed" ||
          outcome === "skipped") && (
          <span className={styles.progressDotIcon}>
            <MilestoneStatusIcon outcome={outcome} size={12} />
          </span>
        )}
      </span>
    );
  }

  // Expired: no lesson popovers / scale hover. Trophy keeps an expiry tooltip.
  const allowLessonPreview = !expired;
  const allowExpiryTooltip = expired && isLast;

  return (
    <>
      <div
        ref={anchorRef}
        className={[
          isLast
            ? styles.progressMilestoneTrophyWrap
            : styles.progressMilestoneWrap,
          allowLessonPreview ? styles.progressMilestoneInteractive : "",
          allowExpiryTooltip ? styles.progressMilestoneExpiredTrophy : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ left: `${leftPercent}%` }}
        title={
          allowExpiryTooltip
            ? undefined
            : allowLessonPreview
              ? training.title || undefined
              : undefined
        }
        onMouseEnter={
          allowLessonPreview || allowExpiryTooltip ? scheduleOpen : undefined
        }
        onMouseLeave={
          allowLessonPreview || allowExpiryTooltip ? scheduleClose : undefined
        }
        onFocus={
          allowLessonPreview || allowExpiryTooltip ? scheduleOpen : undefined
        }
        onBlur={
          allowLessonPreview || allowExpiryTooltip ? scheduleClose : undefined
        }
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {marker}
      </div>
      {allowLessonPreview && open && anchorRect ? (
        <MilestonePreviewPopover
          training={training}
          index={index}
          anchorRect={anchorRect}
          onMouseEnter={keepOpen}
          onMouseLeave={scheduleClose}
        />
      ) : null}
      {allowExpiryTooltip && open && anchorRect ? (
        <ExpiredTrophyTooltip
          anchorRect={anchorRect}
          completedAt={completedAt}
          expiresAt={expiresAt}
        />
      ) : null}
    </>
  );
}

/**
 * Horizontal progress track with a milestone circle per training.
 * Small dots are packed in a zone that stops short of the trophy so the
 * larger final milestone never overlaps neighbors when the module is dense.
 */
export function CertificationTrainingProgress({
  trainings,
  trophyImageUrl,
  moduleComplete = false,
  expired = false,
  completedAt,
  expiresAt,
}: CertificationTrainingProgressProps) {
  const steps = trainings.length;
  const priorTrainings = steps > 1 ? trainings.slice(0, -1) : [];
  const lastTraining = steps > 0 ? trainings[steps - 1] : null;
  const outcomes = withSkippedGaps(
    trainings.map((training, index) =>
      getTrainingAttemptOutcome(
        training,
        moduleComplete && index === trainings.length - 1,
      ),
    ),
  );
  const fill = furthestAttemptFillPercent(outcomes);

  return (
    <div
      className={styles.progressTrack}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={fill}
      aria-label={
        expired ? `Expired — was ${fill}% complete` : `${fill}% complete`
      }
    >
      <div className={styles.progressInner}>
        <div
          className={[
            styles.progressRail,
            expired ? styles.progressRailExpired : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div
            className={[
              styles.progressFill,
              expired ? styles.progressFillExpired : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{ width: `${fill}%` }}
          />
        </div>
        <div className={styles.progressMilestones}>
          {priorTrainings.length > 0 ? (
            <div className={styles.progressMilestoneDots}>
              {priorTrainings.map((training, index) => {
                const leftPercent =
                  priorTrainings.length <= 1
                    ? 0
                    : (index / (priorTrainings.length - 1)) * 100;
                const outcome = outcomes[index] ?? "none";
                return (
                  <TrainingMilestone
                    key={training.id}
                    training={training}
                    index={index}
                    isLast={false}
                    outcome={outcome}
                    trophyImageUrl={trophyImageUrl}
                    leftPercent={leftPercent}
                    expired={expired}
                    completedAt={completedAt}
                    expiresAt={expiresAt}
                  />
                );
              })}
            </div>
          ) : null}
          {lastTraining ? (
            <TrainingMilestone
              key={lastTraining.id}
              training={lastTraining}
              index={steps - 1}
              isLast
              outcome={outcomes[steps - 1] ?? "none"}
              trophyImageUrl={trophyImageUrl}
              leftPercent={100}
              expired={expired}
              completedAt={completedAt}
              expiresAt={expiresAt}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
